import { message } from 'antd';

type HttpResponseError = {
  response: {
    status: number;
    data?: {
      code?: number;
      message?: string | string[];
      errorMessage?: string;
      error?: string;
      msg?: string;
    };
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isHttpResponseError = (error: unknown): error is HttpResponseError => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return false;
  }

  return typeof error.response.status === 'number';
};

export const hasHttpResponseStatus = (
  error: unknown,
  status: number,
): boolean => isHttpResponseError(error) && error.response.status === status;

export const hasHttpResponseCode = (error: unknown, code: number): boolean =>
  isHttpResponseError(error) && error.response.data?.code === code;

/** 优先展示后端业务文案，避免 axios 的 "Request failed with status code"。 */
export const getHttpErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (isHttpResponseError(error)) {
    const data = error.response.data;
    // 逐个候选键取第一个「真人话」文案：BFF 可能把 axios 的
    // "Request failed with status code N" 塞进 message，此时必须继续往后找
    // （errorMessage/error/msg），别让它遮住真正的后端提示。
    const candidates = [
      data?.message,
      data?.errorMessage,
      data?.error,
      data?.msg,
    ];
    for (const raw of candidates) {
      const text = Array.isArray(raw) ? raw.filter(Boolean).join('，') : raw;
      if (
        typeof text === 'string' &&
        text.trim() &&
        !/^request failed/i.test(text)
      ) {
        return text.trim();
      }
    }
  }
  if (
    error instanceof Error &&
    error.message.trim() &&
    !/^request failed/i.test(error.message)
  ) {
    return error.message.trim();
  }
  return fallback;
};

/**
 * 阶段门禁缺项：后端 assertTransitionAllowed / assertFundBatchTransitionAllowed 抛
 * `400 { code: 'CHECKLIST_BLOCKED', message, blockers[] }`，缺项明细在 blockers 里，
 * 只展示 message 会丢掉「到底缺哪几项」这一核心信息。
 */
export const getChecklistBlockers = (error: unknown): string[] => {
  if (!isHttpResponseError(error)) return [];
  const data = error.response.data as
    | { code?: string; blockers?: unknown }
    | undefined;
  if (data?.code !== 'CHECKLIST_BLOCKED' || !Array.isArray(data.blockers)) {
    return [];
  }
  return data.blockers.flatMap((item) => {
    if (!isRecord(item)) return [];
    const text = item.message;
    return typeof text === 'string' && text.trim() ? [text.trim()] : [];
  });
};

/**
 * 全局拦截器（requestErrorConfig）已对请求错误弹过一次 toast 并标记 error.handled，
 * 这里只兜非请求类错误的 fallback，避免同一条错误弹两次。
 *
 * 阶段门禁缺项单独走 `getChecklistBlockers`，由调用方用 Modal 列出全部缺项
 * （toast 撑不住多条，且这类错误需要用户逐项去补）。
 */
export const handleActionError = (
  error: unknown,
  fallback: string,
): boolean => {
  if ((error as { handled?: boolean } | null)?.handled) return false;
  const blockers = getChecklistBlockers(error);
  if (blockers.length) {
    message.error(
      `${getHttpErrorMessage(error, fallback)}：${blockers.join('；')}`,
    );
    return true;
  }
  message.error(getHttpErrorMessage(error, fallback));
  return true;
};
